/**
 * ImportChoiceModal.jsx
 *
 * Pre-upload modal shown when the user clicks "Import Resume".
 * Uses CSS variables so it correctly adapts to both light and dark themes.
 *
 * Props:
 *   isOpen               — boolean
 *   onClose              — () => void
 *   onConfirm            — (mode: 'current' | 'new') => void
 *   hasActiveConversation — boolean, whether a real conversation thread already
 *                           exists for the current document. When false, there
 *                           is nothing to "continue", so that option is hidden
 *                           and 'new' is the only choice.
 */
import React, { useState, useEffect } from 'react';

export default function ImportChoiceModal({ isOpen, onClose, onConfirm, hasActiveConversation = false }) {
  const [mode, setMode] = useState('new');

  useEffect(() => {
    if (isOpen) setMode(hasActiveConversation ? 'current' : 'new');
  }, [isOpen, hasActiveConversation]);

  if (!isOpen) return null;

  const options = hasActiveConversation
    ? [
        {
          id: 'current',
          icon: '💬',
          title: 'Continue in current conversation',
          desc: 'Resume will be attached to your existing AI thread.',
        },
        {
          id: 'new',
          icon: '✨',
          title: 'Start a new AI conversation',
          desc: 'Opens a fresh conversation focused on your imported resume.',
        },
      ]
    : [
        {
          id: 'new',
          icon: '✨',
          title: 'Start a new AI conversation',
          desc: 'No active conversation yet — we\'ll open a fresh one focused on your imported resume.',
        },
      ];

  const handleConfirm = () => onConfirm(mode);
  const handleBackdropClick = (e) => { if (e.target === e.currentTarget) onClose(); };

  return (
    <div
      onClick={handleBackdropClick}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9990,
        background: 'rgba(0,0,0,0.55)',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: "'Inter', sans-serif",
        padding: 24,
      }}
    >
      <div style={{
        background: 'var(--surface-1, #ffffff)',
        border: '1px solid var(--card-border, rgba(0,0,0,0.08))',
        borderRadius: 20,
        padding: '36px 40px',
        width: '100%',
        maxWidth: 420,
        boxShadow: '0 24px 64px rgba(0,0,0,0.18)',
        display: 'flex',
        flexDirection: 'column',
        gap: 24,
        animation: 'icm-pop 0.25s cubic-bezier(0.16,1,0.3,1)',
      }}>

        {/* Header */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{
              width: 40, height: 40, borderRadius: 12,
              background: 'rgba(252,163,17,0.10)',
              border: '1px solid rgba(252,163,17,0.20)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 18,
            }}>
              📎
            </div>
            <h2 style={{
              fontSize: 16, fontWeight: 700,
              color: 'var(--text-primary)',
              margin: 0,
            }}>
              Import Resume
            </h2>
          </div>
          <p style={{
            fontSize: 12.5,
            color: 'var(--text-muted)',
            lineHeight: 1.6, margin: 0,
          }}>
            Choose how you'd like to import. We'll analyze your resume and open the AI workspace automatically.
          </p>
        </div>

        {/* Options */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {options.map((opt) => {
            const active = mode === opt.id;
            return (
              <button
                key={opt.id}
                onClick={() => setMode(opt.id)}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 14,
                  padding: '14px 16px',
                  borderRadius: 12,
                  border: active
                    ? '1.5px solid rgba(252,163,17,0.6)'
                    : '1.5px solid var(--card-border)',
                  background: active
                    ? 'rgba(252,163,17,0.08)'
                    : 'var(--surface-2, transparent)',
                  cursor: 'pointer',
                  textAlign: 'left',
                  transition: 'all 0.15s ease',
                  width: '100%',
                }}
              >
                {/* Radio dot */}
                <div style={{
                  width: 18, height: 18, borderRadius: '50%',
                  border: active ? '2px solid var(--brand)' : '2px solid var(--text-muted)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0, marginTop: 2,
                  transition: 'all 0.15s ease',
                }}>
                  {active && (
                    <div style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--brand)' }} />
                  )}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ fontSize: 14 }}>{opt.icon}</span>
                    <span style={{
                      fontSize: 13, fontWeight: 600,
                      color: active ? 'var(--text-primary)' : 'var(--text-secondary)',
                      transition: 'color 0.15s',
                    }}>
                      {opt.title}
                    </span>
                  </div>
                  <span style={{
                    fontSize: 11.5,
                    color: 'var(--text-muted)',
                    lineHeight: 1.5,
                  }}>
                    {opt.desc}
                  </span>
                </div>
              </button>
            );
          })}
        </div>

        {/* Flow preview */}
        <div style={{
          background: 'var(--surface-2)',
          border: '1px solid var(--card-border)',
          borderRadius: 10,
          padding: '12px 16px',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          flexWrap: 'wrap',
        }}>
          {['Upload', 'Parse', 'Review', 'Open AI Chat'].map((step, i, arr) => (
            <React.Fragment key={step}>
              <span style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 500 }}>{step}</span>
              {i < arr.length - 1 && (
                <span style={{ fontSize: 10, color: 'var(--text-muted)', opacity: 0.5 }}>→</span>
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', gap: 10 }}>
          <button
            onClick={onClose}
            style={{
              flex: 1, padding: '11px 0', borderRadius: 10,
              background: 'var(--surface-2)',
              border: '1px solid var(--card-border)',
              color: 'var(--text-secondary)',
              fontSize: 13, fontWeight: 500,
              cursor: 'pointer', transition: 'all 0.15s',
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            style={{
              flex: 2, padding: '11px 0', borderRadius: 10,
              background: 'linear-gradient(135deg, var(--brand), #f59e0b)',
              border: 'none', color: '#ffffff',
              fontSize: 13, fontWeight: 700,
              cursor: 'pointer',
              boxShadow: '0 4px 16px rgba(252,163,17,0.35)',
              transition: 'all 0.15s',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.01)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
          >
            Choose File & Import →
          </button>
        </div>
      </div>

      <style>{`
        @keyframes icm-pop {
          from { opacity: 0; transform: scale(0.94) translateY(8px); }
          to   { opacity: 1; transform: scale(1) translateY(0); }
        }
      `}</style>
    </div>
  );
}
